import { ChangeDetectorRef } from '@angular/core';
import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ValidatorFn } from '@angular/forms';
import { AbstractControl } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-smartire',
    templateUrl: './smartire.component.html',
    styleUrls: ['./smartire.component.scss'],
    standalone: false
})
export class SmartireComponent {
  service_cost_per_vehicle: FormControl;
  roiCalculatorForm: FormGroup;
  results: {
    res1_norm: number;
    res1_smar: number;
    res1_save: number;
    res2_norm: number;
    res2_smar: number;
    res2_save: number;
    res3_norm: number;
    res3_smar: number;
    res3_save: number;
    res4_norm: number;
    res4_smar: number;
    res4_save: number;
    tot_norm: number;
    tot_smar: number;
    tot_save: number;
    tot_anual: number;
  };
  control: AbstractControl<any, any>;
  constructor(private formBuilder: FormBuilder, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.roiCalculatorForm = this.formBuilder.group({
      roi_number_vehicles: ['100', [Validators.required, this.customValidator(1, 1000000)]],
      roi_avg_mileage: ['75000', [Validators.required, this.customValidator(  1, 100000)]],
      roi_number_tires: ['8', [Validators.required, this.customValidator(2, 30)]],
      roi_inspection: ['2', [Validators.required, this.customValidator(1, 30)]],
      roi_inflation: ['15', [Validators.required, this.customValidator(1, 30)]],
      roi_downtime: ['1', [Validators.required, this.customValidator(1, 30)]],
      roi_downtime_vehicles: ['2', [Validators.required, this.customValidator(1, 30)]],
      roi_tire_life: ['125000', [Validators.required, this.customValidator(1, 300000)]],
      roi_fuel_consumption: ['7.35', [Validators.required, this.customValidator(1, 100)]],
      
      roi_cost_fuel: ['2.70', [Validators.required, this.customValidator(1, 20)]],
      roi_cost_tire: ['350.00', [Validators.required, this.customValidator(1, 5000)]],
      roi_cost_downtime: ['500.00', [Validators.required, this.customValidator(1, 10000)]],
      service_cost_per_vehicle: ['4'],
      roi_service_minutes: ['2', [Validators.required, this.customValidator(1, 60)]],
      roi_service_perhour: ['15', [Validators.required, this.customValidator(1, 300)]],
      roi_reduce: ['40'],
    });
  }
  onSubmit() {
    if (this.roiCalculatorForm.valid) {
      this.calculateROI();
    } else {
      // Form is invalid, display error messages or handle accordingly
      console.error('Form is invalid');
    }
  }
  onFieldFocus(event: Event, DefaultValue, controlName: string) {
    const currentValue = (event.target as HTMLInputElement).value;
    this.control = this.roiCalculatorForm.get(controlName);

    if (currentValue == DefaultValue) {
      this.control.setValue('');
    }
    this.control.markAsDirty();
  }

  customValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (!value || isNaN(value) || value < min || value > max || value.includes(',') || value.includes('-')) {
        return { customValidation: true };
      }
      return null;
    };
  }

  calculateServiceCost(): void {
    const form = this.roiCalculatorForm.value;
    // Get values from form inputs
    const D18 = form.roi_service_minutes;
    const H18 = form.roi_service_perhour;
    const B6 = form.roi_number_tires;

    // Calculate service cost per vehicle
    const B18 = D18 * B6 * (H18 / 60);
    // Format the calculated value
    const formattedServiceCost = this.CurrencyFormatted(B18).replace('$', '');
    this.roiCalculatorForm.controls['service_cost_per_vehicle'].setValue(formattedServiceCost);
    this.cdr.detectChanges();
    // Update the form input with the calculated value
  }

  calculateROI() {
    const form = this.roiCalculatorForm.value;
    const B4 = form.roi_number_vehicles;
    const B5 = form.roi_avg_mileage;
    const B6 = form.roi_number_tires;
    const B7 = form.roi_inspection;
    const B8 = form.roi_inflation;
    const B9 = form.roi_downtime;
    const B10 = form.roi_tire_life;
    const B11 = form.roi_fuel_consumption;
    const F9 = form.roi_downtime_vehicles;
    const B15 = form.roi_cost_fuel;
    const B16 = form.roi_cost_tire;
    const B17 = form.roi_cost_downtime;
    const D18 = form.roi_service_minutes;
    const H18 = form.roi_service_perhour;
    const B18 = D18 * B6 * (H18 / 60);
    const M19 = form.roi_reduce;

    const res1_norm = ((((B5 * 10) / ((B10 * (100 - B8 * 1.25)) / 100)) * B6 * B16) / 10) * B4;
    const res1_smar = ((((B5 * 10) / B10) * B6 * B16) / 10) * B4;
    const res1_save = res1_norm - res1_smar;

    const res2_norm = (B15 / (((100 - B8 * 0.1) / 100) * B11)) * B5 * B4;
    const res2_smar = (B15 / B11) * B5 * B4;
    const res2_save = res2_norm - res2_smar;

    const res3_norm = B18 * B7 * 12 * B4;
    const res3_smar = ((100 - M19) / 100) * res3_norm;
    const res3_save = res3_norm - res3_smar;

    const res4_norm = (B9 / F9) * B4 * B17;
    const res4_smar = res4_norm * 0.52;
    const res4_save = res4_norm - res4_smar;

    const tot_norm = res1_norm + res2_norm + res3_norm + res4_norm;
    const tot_smar = res1_smar + res2_smar + res3_smar + res4_smar;
    const tot_save = res1_save + res2_save + res3_save + res4_save;
    const tot_anual = tot_save / B4;

    this.results = {
      res1_norm: res1_norm,
      res1_smar: res1_smar,
      res1_save: res1_save,
      res2_norm: res2_norm,
      res2_smar: res2_smar,
      res2_save: res2_save,
      res3_norm: res3_norm,
      res3_smar: res3_smar,
      res3_save: res3_save,
      res4_norm: res4_norm,
      res4_smar: res4_smar,
      res4_save: res4_save,
      tot_norm: tot_norm,
      tot_smar: tot_smar,
      tot_save: tot_save,
      tot_anual: tot_anual,
    };
  }

  CurrencyFormatted(amount: number): string {
    if (amount != null || undefined) {
      // Round to 2 decimal places
      const roundedAmount = amount.toFixed(2);

      // Add commas for thousands separator
      const parts = roundedAmount.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      // Join the parts and return
      return '$ ' + parts.join('.');
    } else {
      return '';
    }
  }
}
